// react
import { useState } from "react";
// redux
import { useAppDispatch } from "../../../redux/hooks";
import { itemsRemoved, INopimsBasketItemModel } from "../nopimsBasketSlice";
// kendo
import { Grid, GridColumn, GridPageChangeEvent } from "@progress/kendo-react-grid";
import { process, State } from "@progress/kendo-data-query";
// common
import CustomGridCell from "../../../common/kendoGrid/CustomGridCell";
import ExternalLinkView from "../../../common/externalLink/ExternalLinkView";
import * as GlobalHelpers from "../../../common/GlobalHelpers";

interface IProps {
    items: INopimsBasketItemModel[];
    onShowSubmitBasket: () => void;
    onExport: () => void;
}

export default function BasketView(props: IProps): React.JSX.Element {
    const { items } = props;
    const dispatch = useAppDispatch();

    // grid state
    const [pageSize, setPageSize] = useState(10);
    const initialGridState = {
        sort: [{ field: "title", dir: "asc" }],
        skip: 0,
        take: pageSize,
    } as State;
    const [gridState, setGridState] = useState(initialGridState);

    function onItemRemoved(item: INopimsBasketItemModel): void {
        dispatch(itemsRemoved([item]));
    }

    function onItemsCleared(): void {
        dispatch(itemsRemoved(items));
    }

    return (
        <>
            <div className="d-flex justify-content-between">
                <div className="mt-2">
                    {items.length > 0 ? (
                        <>
                            Items with a link in the Download column are available via the link to be downloaded. Any items without a link must be requested, there will be a cost associated with the
                            order.
                        </>
                    ) : (
                        <>Your basket is empty.</>
                    )}
                </div>
                <button className="btn btn-primary mb-2 me-2 px-2" type="button" disabled={items.length === 0} onClick={() => props.onExport()} title="Click to export the NOPIMS Basket list.">
                    Export Basket listing
                </button>
            </div>
            <Grid
                pageable={{ pageSizes: [10, 20, 50, 100], pageSizeValue: pageSize, responsive: false }}
                onPageChange={(e: GridPageChangeEvent) => {
                    setPageSize(e.page.take);
                    setGridState({ ...gridState, take: e.page.take, skip: e.page.skip });
                }}
                sortable
                resizable
                navigatable
                data={process(items, gridState)}
                {...gridState}
                onDataStateChange={(e) => {
                    setGridState(e.dataState);
                }}
            >
                <GridColumn
                    field="infoUrl"
                    title="Info"
                    width={90}
                    cell={(props) => <CustomGridCell gridCellProps={props}>{props.dataItem.infoUrl && <ExternalLinkView href={props.dataItem.infoUrl}>Listing</ExternalLinkView>}</CustomGridCell>}
                />
                <GridColumn
                    field="downloadUrl"
                    title="Download"
                    width={160}
                    cell={(props) => (
                        <CustomGridCell gridCellProps={props}>
                            {props.dataItem.downloadUrl && <ExternalLinkView href={props.dataItem.downloadUrl}>{props.dataItem.downloadName}</ExternalLinkView>}
                        </CustomGridCell>
                    )}
                />
                <GridColumn
                    field="fileSizeBytes"
                    title="File Size"
                    width={140}
                    cell={(props) => <CustomGridCell gridCellProps={props}>{props.dataItem.fileSizeBytes && GlobalHelpers.convertBytesToString(props.dataItem.fileSizeBytes)}</CustomGridCell>}
                />
                <GridColumn field="itemId" title="Item ID" width={200} />
                <GridColumn field="dataDescription" title="Data Description" width={400} />
                <GridColumn field="documentType" title="Document Type" width={160} />
                <GridColumn field="type" title="Report/Data Type" width={200} />
                <GridColumn field="activity" title="Activity Type" width={110} />
                <GridColumn field="activityIdentifier" title="Activity ID" width={130} />
                <GridColumn field="activityName" title="Activity Name" width={300} />
                <GridColumn
                    width={82}
                    cell={(gridCellProps) => (
                        <CustomGridCell gridCellProps={gridCellProps} className={gridCellProps.className + " py-1"} style={gridCellProps.style}>
                            <button
                                className="btn btn-secondary btn-sm"
                                type="button"
                                onClick={() => onItemRemoved(gridCellProps.dataItem)}
                                title={`Click to remove this ${gridCellProps.dataItem.activity}'s Document from the Basket.`}
                            >
                                Remove
                            </button>
                        </CustomGridCell>
                    )}
                />
            </Grid>
            <div className="mt-2">
                <button className="btn btn-primary ms-2 px-2" type="button" disabled={items.length === 0} onClick={() => onItemsCleared()} title="Click to Remove All Documents from the Basket.">
                    Remove All
                </button>
                <button
                    className="btn btn-primary ms-2 px-2"
                    type="button"
                    disabled={items.length === 0}
                    onClick={() => props.onShowSubmitBasket()}
                    title="Click to proceed to the Request form to submit the Basket."
                >
                    Proceed
                </button>
            </div>
        </>
    );
}
